/**
 * @file: OrderLinkButton.tsx
 *
 * The room-detail header "open order" link, shared by the mobile nav header
 * (set via the page's setOptions), the desktop PaneHeader, and the floating
 * chat panel. Self-contained: it owns its navigation (finished order → service
 * record, otherwise → task detail).
 *
 * Pushes via the app-root navigation ref (`getGlobalNavigationRef`) rather than
 * the nearest navigator: the floating chat panel runs its own standalone
 * NavigationContainer, so a parent-walked `useNavigation` would dead-end inside
 * it and never reach the real `Task`/`ServiceRecord` routes. The root ref hits
 * the app stack from any container.
 *
 * Self-hides when the order it links to is already the screen on view (the
 * active task / service record) — redundant there. That's how the floating
 * panel (which sits over its own task detail) drops the link.
 */

import { Typography } from '@euler/app/components/typography';
import { getGlobalNavigationRef } from '@euler/app/navigation/RoutesContainer';
import {
  ActiveEntity,
  sameActiveEntity,
  useActiveEntity$,
} from '@euler/functions/ActiveEntityContext';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { StackActions } from '@react-navigation/native';
import { FC, memo, useCallback } from 'react';
import { TouchableOpacity } from 'react-native';
import { OrderLinkTarget } from '../functions/useRoomOrder';

export const OrderLinkButton: FC<{ target: OrderLinkTarget }> = memo(
  ({ target }) => {
    const { rpx } = useResponsiveContext();
    const activeEntity$ = useActiveEntity$();
    const [activeEntity] = useBehaviorSubject(activeEntity$);

    const orderEntity: ActiveEntity =
      target.kind === 'record'
        ? { kind: 'serviceRecord', serviceRecordId: target.id }
        : { kind: 'task', taskNo: target.taskNo };

    const onPress = useCallback(() => {
      const root = getGlobalNavigationRef();
      if (!root?.isReady()) return;
      root.dispatch(
        target.kind === 'record'
          ? StackActions.push('ServiceRecord', { id: target.id })
          : StackActions.push('Task', { taskNo: target.taskNo }),
      );
    }, [target]);

    // Already viewing this order → the link is redundant.
    if (sameActiveEntity(activeEntity, orderEntity)) return null;

    return (
      <TouchableOpacity
        onPress={onPress}
        hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
        style={{
          paddingVertical: rpx(4),
          paddingLeft: rpx(8),
          paddingRight: rpx(16),
        }}
      >
        <Typography.Text size={rpx(13)} color="#3b82f6">
          {target.label}
        </Typography.Text>
      </TouchableOpacity>
    );
  },
);
